import type {
  AspectRatio,
  CalendarMediaType,
  CalendarPlatform,
  CalendarVideoLength,
} from "@/types/contentCalendar.type";
import { isAutoPublishablePlatform } from "./publicationIntegrity";

/**
 * Web mirror of the backend's domain/Scheduling/channelWarnings.ts — advisory
 * only, so the picker can warn live without a round-trip. The server still
 * computes its own on accept/edit; keep the two in sync.
 */

export type ChannelWarningLevel = "HARD" | "SOFT" | "INFO";
export interface ChannelWarning {
  platform: CalendarPlatform;
  level: ChannelWarningLevel;
  message: string;
}

/**
 * Channels that only accept a portrait frame.
 *
 * Unreachable while Snapchat is parked — a channel that is not auto-publishable
 * returns above before the frame rules run, exactly as the backend does. Kept
 * so the two mirrors stay symmetric when the capability comes back.
 */
const PORTRAIT_ONLY: ReadonlySet<CalendarPlatform> = new Set(["SNAPCHAT"]);

/** Channels that can't take a static image. */
const NO_IMAGE: ReadonlySet<CalendarPlatform> = new Set(["YOUTUBE"]);

export function channelWarnings(input: {
  mediaType: CalendarMediaType;
  aspectRatio: AspectRatio;
  videoLength: CalendarVideoLength | null;
  channels: CalendarPlatform[];
}): ChannelWarning[] {
  const warnings: ChannelWarning[] = [];

  for (const channel of input.channels) {
    if (!isAutoPublishablePlatform(channel)) {
      warnings.push({
        platform: channel,
        level: "INFO",
        message: `${channel} is not auto-publishable — it will be listed as a post-manually reminder.`,
      });
      continue;
    }
    if (input.mediaType === "STATIC" && NO_IMAGE.has(channel)) {
      warnings.push({
        platform: channel,
        level: "HARD",
        message: `${channel} can't take images — this is a static post.`,
      });
      continue;
    }
    if (
      input.mediaType === "VIDEO" &&
      channel === "YOUTUBE" &&
      input.aspectRatio !== "16:9" &&
      input.videoLength === "SHORT_FORM"
    ) {
      warnings.push({
        platform: channel,
        level: "SOFT",
        message: `${input.aspectRatio} short-form video will surface on YouTube as a Short.`,
      });
    }

    if (PORTRAIT_ONLY.has(channel) && input.aspectRatio === "16:9") {
      warnings.push({
        platform: channel,
        level: "SOFT",
        message: `${channel} does not accept 16:9 — choose a portrait canvas before production.`,
      });
    }
  }

  return warnings;
}
